/**
 * Organization Service
 *
 * Unified service for all organization operations with comprehensive validation.
 * Consolidates basic CRUD operations with enhanced Zod validation.
 *
 * @version 2.0.0
 * @last-updated 2026-02-05
 */

import { typedSupabase } from "@/lib/typed-supabase";
import {
  createSuccessResponse,
  createErrorResponse,
  ServiceResponse,
} from "@/services/core/service-registry";
import {
  Organization,
  OrganizationMember,
  OrganizationSetting,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  AddOrganizationMemberRequest,
  UpdateOrganizationMemberRequest,
  OrganizationWithDetails,
  OrganizationSubscriptionTier,
  OrgType,
} from "@/types/organization";
import {
  createOrganizationSchema,
  updateOrganizationSchema,
  CreateOrganizationInput,
  generateValidSlug,
  isValidSlug,
  isReservedSlug,
  generateUniqueSlug,
} from "@flowstate/types/organization-validation";
import type { Json } from "@/integrations/supabase/database.types";
import { logger } from "@/lib/logger";

import { parseErrorMessage } from "@/utils/errorUtils";

/**
 * Validation result interface
 */
interface ValidationResult {
  isValid: boolean;
  error?: string;
  finalSlug?: string;
}

/**
 * Unified Organization Service
 * Handles all organization CRUD operations with comprehensive validation
 */
class OrganizationService {
  /**
   * Rate limit configuration
   */
  private readonly RATE_LIMIT = {
    max: 3,
    windowMs: 60000, // 1 minute
  };

  /**
   * Generate slug from name (legacy method for backwards compatibility)
   * @deprecated Use generateValidSlug from organization-validation instead
   */
  private generateSlug(name: string): string {
    return generateValidSlug(name);
  }

  /**
   * Validate organization data before creation
   * Checks schema, slug availability, reserved words, and uniqueness
   *
   * @param data - Organization data to validate
   * @returns Validation result with final slug if successful
   */
  private async validateOrganizationData(
    data: CreateOrganizationInput
  ): Promise<ValidationResult> {
    // Validate with Zod schema
    const validationResult = createOrganizationSchema.safeParse(data);

    if (!validationResult.success) {
      const error = validationResult.error.errors[0]?.message || "Invalid organization data";
      return { isValid: false, error };
    }

    const validatedData = validationResult.data;

    // Generate slug if not provided
    const finalSlug = validatedData.slug ?? generateValidSlug(validatedData.name);

    // Additional server-side validations
    if (!isValidSlug(finalSlug)) {
      return { isValid: false, error: "Invalid slug format" };
    }

    if (isReservedSlug(finalSlug)) {
      return { isValid: false, error: "This slug is reserved and cannot be used" };
    }

    // Check uniqueness in database
    const { data: existingOrg, error: checkError } = await typedSupabase
      .from("organizations")
      .select("slug")
      .eq("slug", finalSlug)
      .single();

    if (checkError && checkError.code !== "PGRST116") {
      // PGRST116 = Not found error, which is expected
      return { isValid: false, error: "Failed to validate slug uniqueness" };
    }

    if (existingOrg) {
      // Generate unique slug with numeric suffix
      const { data: orgsWithSimilarSlug } = await typedSupabase
        .from("organizations")
        .select("slug")
        .like("slug", `${finalSlug}-%`);

      const existingSlugs = [
        finalSlug,
        ...(orgsWithSimilarSlug?.map((o: { slug: string }) => o.slug) ?? []),
      ];
      const uniqueSlug = generateUniqueSlug(finalSlug, existingSlugs);

      return { isValid: true, finalSlug: uniqueSlug };
    }

    return { isValid: true, finalSlug };
  }

  /**
   * Check rate limits for user
   * Prevents abuse by limiting organization creation rate
   *
   * @param userId - User ID to check
   * @returns True if user can create org, false if rate limit exceeded
   */
  private async checkRateLimit(userId: string): Promise<boolean> {
    const { data: recentOrgs } = await typedSupabase
      .from("organizations")
      .select("id")
      .eq("created_by", userId)
      .gte("created_at", new Date(Date.now() - this.RATE_LIMIT.windowMs).toISOString());

    return (recentOrgs?.length ?? 0) < this.RATE_LIMIT.max;
  }

  /**
   * Get all organizations for the current user
   * Returns only active organizations user is a member of
   *
   * @returns Promise with list of organizations or error
   */
  async getUserOrganizations(): Promise<ServiceResponse<Organization[]>> {
    try {
      const { data: { user } } = await typedSupabase.auth.getUser();
      if (!user) {
        return createErrorResponse("User not authenticated");
      }

      // Fetch organizations and memberships in a single query using an inner join
      const { data: organizations, error } = await typedSupabase
        .from("organizations")
        .select(`*, organization_members!inner(user_id)`)
        .eq("organization_members.user_id", user.id)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) {throw error;}

      // Remove the joined data from the result
      const cleanedOrganizations = (organizations || []).map((org) => {
        const { organization_members, ...rest } = org as unknown as Record<string, unknown>;
        return rest;
      }) as unknown as Organization[];

      return createSuccessResponse(cleanedOrganizations || []);
    } catch (error) {
      logger.error("Error fetching user organizations:", { error });
      return createErrorResponse(parseErrorMessage(error) || "Failed to fetch organizations");
    }
  }

  /**
   * Get a single organization by ID with detailed counts
   *
   * @param id - Organization ID
   * @returns Promise with organization details or error
   */
  async getOrganizationById(id: string): Promise<ServiceResponse<OrganizationWithDetails>> {
    try {
      // Run all queries in parallel for better performance
      const [
        { data: organization, error },
        { count: memberCount, error: memberError },
        { count: projectCount, error: projectError },
        { count: clientCount, error: clientError }
      ] = await Promise.all([
        typedSupabase.from("organizations").select("*").eq("id", id).single(),
        typedSupabase.from("organization_members").select("*", { count: "exact", head: true }).eq("organization_id", id),
        typedSupabase.from("projects").select("*", { count: "exact", head: true }).eq("organization_id", id),
        typedSupabase.from("clients").select("*", { count: "exact", head: true }).eq("organization_id", id)
      ]);

      if (error) {throw error;}
      if (memberError) {throw memberError;}
      if (projectError) {throw projectError;}
      if (clientError) {throw clientError;}

      const organizationWithDetails: OrganizationWithDetails = {
        ...(organization as Organization),
        member_count: memberCount ?? 0,
        project_count: projectCount ?? 0,
        client_count: clientCount ?? 0,
      };

      return createSuccessResponse(organizationWithDetails);
    } catch (error) {
      logger.error(`Error fetching organization ${id}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to fetch organization');
    }
  }

  /**
   * Create a new organization with full validation
   * Validates data, checks rate limits, generates unique slug, and creates org via RPC
   *
   * @param data - Organization creation data
   * @returns Promise with created organization or error
   */
  async createOrganization(
    data: CreateOrganizationRequest
  ): Promise<ServiceResponse<Organization>> {
    try {
      const { data: { user }, error: authError } = await typedSupabase.auth.getUser();
      if (authError || !user) {
        return createErrorResponse("User not authenticated");
      }

      // Verify user ID is available
      if (!user.id) {
        return createErrorResponse("User ID not available");
      }

      // Validate input data with enhanced validation
      const validationInput: CreateOrganizationInput = {
        name: data.name,
        description: data.description ?? undefined,
        slug: data.slug ?? undefined,
        logo_url: data.logo_url ?? undefined,
        settings: (data.settings as Record<string, unknown> | undefined) ?? {},
        subscription_tier: (data.subscription_tier ?? "free") as CreateOrganizationInput['subscription_tier'],
      };
      const validation = await this.validateOrganizationData(validationInput);
      if (!validation.isValid || !validation.finalSlug) {
        return createErrorResponse(validation.error ?? "Validation failed");
      }

      // Check rate limiting
      const canCreate = await this.checkRateLimit(user.id);
      if (!canCreate) {
        return createErrorResponse(
          "Rate limit exceeded: Please wait before creating another organization"
        );
      }

      // Verify session is active
      const { data: { session } } = await typedSupabase.auth.getSession();
      if (!session) {
        return createErrorResponse("No active session. Please log in again.");
      }

      // Ensure user.id matches session user
      if (session.user.id !== user.id) {
        logger.warn("User ID mismatch between getUser() and session");
      }

      // Use the bypass function to create organization (handles RLS correctly)
      const { data: organizationId, error: rpcError } = await typedSupabase
        .rpc('create_organization_bypass_rls', {
          p_name: data.name,
          p_slug: validation.finalSlug,
          p_description: data.description,
          p_logo_url: data.logo_url,
          p_settings: ((data.settings as Record<string, unknown> | undefined) ?? {}) as Json,
          p_subscription_tier: (data.subscription_tier ?? "free") as CreateOrganizationInput['subscription_tier'],
          p_created_by: session.user.id,
          p_org_type: (data.org_type ?? "team") as string,
        });

      if (rpcError) {
        logger.error("Error creating organization via RPC:", {
          error: rpcError,
          errorCode: rpcError.code,
          errorMessage: rpcError.message,
          userId: session.user.id,
        });
        throw rpcError;
      }

      // Fetch the created organization
      const { data: newOrganization, error: fetchError } = await typedSupabase
        .from("organizations")
        .select("*")
        .eq("id", organizationId as string)
        .single();

      if (fetchError) {throw fetchError;}

      return createSuccessResponse(newOrganization as Organization);
    } catch (error) {
      logger.error("Error creating organization:", { error });
      return createErrorResponse(parseErrorMessage(error) || "Failed to create organization");
    }
  }

  /**
   * Update an existing organization with validation
   * Note: Slug cannot be updated after creation (immutable)
   *
   * @param id - Organization ID
   * @param data - Update data
   * @returns Promise with updated organization or error
   */
  async updateOrganization(
    id: string,
    data: UpdateOrganizationRequest
  ): Promise<ServiceResponse<Organization>> {
    try {
      // Validate update data with Zod
      const validationResult = updateOrganizationSchema.safeParse(data);

      if (!validationResult.success) {
        const error = validationResult.error.errors[0]?.message || "Invalid update data";
        return createErrorResponse(error);
      }

      const validatedData = validationResult.data;
      const updateData: Partial<Organization> & {
        updated_at: string;
      } = {
        updated_at: new Date().toISOString(),
      };

      // Map validated fields (slug is explicitly excluded in schema)
      if (validatedData.name !== undefined) {updateData.name = validatedData.name;}
      if (validatedData.description !== undefined) {updateData.description = validatedData.description;}
      if (validatedData.logo_url !== undefined) {updateData.logo_url = validatedData.logo_url;}
      if (validatedData.settings !== undefined) {updateData.settings = validatedData.settings as Json;}
      if (validatedData.subscription_tier !== undefined) {updateData.subscription_tier = validatedData.subscription_tier as OrganizationSubscriptionTier;}

      const { data: updatedOrganization, error } = await typedSupabase
        .from("organizations")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) {throw error;}

      return createSuccessResponse(updatedOrganization as Organization);
    } catch (error) {
      logger.error(`Error updating organization ${id}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to update organization');
    }
  }

  /**
   * Soft delete an organization (sets is_active = false)
   * Data is preserved for recovery and audit purposes
   *
   * @param id - Organization ID
   * @returns Promise with success status or error
   */
  async deleteOrganization(id: string): Promise<ServiceResponse<boolean>> {
    try {
      const { error } = await typedSupabase
        .from("organizations")
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq("id", id);

      if (error) {throw error;}

      return createSuccessResponse(true);
    } catch (error) {
      logger.error(`Error deleting organization ${id}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to delete organization');
    }
  }

  /**
   * Permanently delete an organization (admin only)
   * WARNING: This action cannot be undone
   *
   * @param id - Organization ID
   * @returns Promise with success status or error
   */
  async permanentDeleteOrganization(id: string): Promise<ServiceResponse<boolean>> {
    try {
      const { error } = await typedSupabase
        .from("organizations")
        .delete()
        .eq("id", id);

      if (error) {throw error;}

      return createSuccessResponse(true);
    } catch (error) {
      logger.error(`Error permanently deleting organization ${id}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to permanently delete organization');
    }
  }

  /**
   * Get organization members with user profile details
   *
   * @param organizationId - Organization ID
   * @returns Promise with list of members or error
   */
  async getOrganizationMembers(
    organizationId: string
  ): Promise<ServiceResponse<OrganizationMember[]>> {
    try {
      const { data: members, error } = await typedSupabase
        .from("organization_members")
        .select(
          `
          *,
          user:profiles!user_id (
            id,
            full_name,
            email,
            avatar_url
          )
        `
        )
        .eq("organization_id", organizationId)
        .order("joined_at", { ascending: false });

      if (error) {throw error;}

      return createSuccessResponse(members as OrganizationMember[]);
    } catch (error) {
      logger.error(`Error fetching members for organization ${organizationId}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to fetch organization members');
    }
  }

  /**
   * Add a member to an organization
   *
   * @param organizationId - Organization ID
   * @param data - Member data (user_id and role)
   * @returns Promise with created member record or error
   */
  async addMember(
    organizationId: string,
    data: AddOrganizationMemberRequest
  ): Promise<ServiceResponse<OrganizationMember>> {
    try {
      const { data: { user } } = await typedSupabase.auth.getUser();
      if (!user) {
        return createErrorResponse("User not authenticated");
      }

      const { data: member, error } = await typedSupabase
        .from("organization_members")
        .insert({
          organization_id: organizationId,
          user_id: data.user_id,
          role: data.role,
          invited_by: user.id,
        })
        .select()
        .single();

      if (error) {throw error;}

      return createSuccessResponse(member as OrganizationMember);
    } catch (error) {
      logger.error(`Error adding member to organization ${organizationId}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to add member to organization');
    }
  }

  /**
   * Update a member's role in an organization
   *
   * @param organizationId - Organization ID
   * @param userId - User ID of the member
   * @param data - Updated role data
   * @returns Promise with updated member record or error
   */
  async updateMemberRole(
    organizationId: string,
    userId: string,
    data: UpdateOrganizationMemberRequest
  ): Promise<ServiceResponse<OrganizationMember>> {
    try {
      const { data: member, error } = await typedSupabase
        .from("organization_members")
        .update({
          role: data.role,
        })
        .eq("organization_id", organizationId)
        .eq("user_id", userId)
        .select()
        .single();

      if (error) {throw error;}

      return createSuccessResponse(member as OrganizationMember);
    } catch (error) {
      logger.error(`Error updating member role in organization ${organizationId}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to update member role');
    }
  }

  /**
   * Remove a member from an organization
   *
   * @param organizationId - Organization ID
   * @param userId - User ID of the member to remove
   * @returns Promise with success status or error
   */
  async removeMember(
    organizationId: string,
    userId: string
  ): Promise<ServiceResponse<boolean>> {
    try {
      const { error } = await typedSupabase
        .from("organization_members")
        .delete()
        .eq("organization_id", organizationId)
        .eq("user_id", userId);

      if (error) {throw error;}

      return createSuccessResponse(true);
    } catch (error) {
      logger.error(`Error removing member from organization ${organizationId}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to remove member from organization');
    }
  }

  /**
   * Get user's role in an organization
   *
   * @param organizationId - Organization ID
   * @returns Promise with role string or null if not a member
   */
  async getUserRole(
    organizationId: string
  ): Promise<ServiceResponse<string | null>> {
    try {
      const { data: { user } } = await typedSupabase.auth.getUser();
      if (!user) {
        return createErrorResponse("User not authenticated");
      }

      const { data: member, error } = await typedSupabase
        .from("organization_members")
        .select("role")
        .eq("organization_id", organizationId)
        .eq("user_id", user.id)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          // No member found - user is not a member
          return createSuccessResponse(null);
        }
        throw error;
      }

      return createSuccessResponse(member?.role || null);
    } catch (error) {
      logger.error(`Error fetching user role for organization ${organizationId}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to fetch user role');
    }
  }

  /**
   * Get organization settings as key-value pairs
   *
   * @param organizationId - Organization ID
   * @returns Promise with list of settings or error
   */
  async getOrganizationSettings(
    organizationId: string
  ): Promise<ServiceResponse<OrganizationSetting[]>> {
    try {
      const { data: settings, error } = await typedSupabase
        .from("organization_settings")
        .select("*")
        .eq("organization_id", organizationId);

      if (error) {throw error;}

      return createSuccessResponse(settings as OrganizationSetting[]);
    } catch (error) {
      logger.error(`Error fetching settings for organization ${organizationId}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to fetch organization settings');
    }
  }

  /**
   * Set or update an organization setting
   * Uses upsert to handle both create and update cases
   *
   * @param organizationId - Organization ID
   * @param key - Setting key
   * @param value - Setting value (JSONB)
   * @returns Promise with setting record or error
   */
  async setOrganizationSetting(
    organizationId: string,
    key: string,
    value: Json
  ): Promise<ServiceResponse<OrganizationSetting>> {
    try {
      const { data: setting, error } = await typedSupabase
        .from("organization_settings")
        .upsert(
          {
            organization_id: organizationId,
            key,
            value,
          },
          {
            onConflict: "organization_id,key",
          }
        )
        .select()
        .single();

      if (error) {throw error;}

      return createSuccessResponse(setting as OrganizationSetting);
    } catch (error) {
      logger.error(`Error setting organization setting ${key}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to set organization setting');
    }
  }
  /**
   * Link a Google Workspace domain to an organization.
   * Only team and enterprise orgs can have a linked domain.
   * The domain must be unique across all organizations.
   *
   * @param organizationId - Organization ID
   * @param domain - Google Workspace domain (e.g. 'acme.com')
   * @returns Promise with updated organization or error
   */
  async linkGoogleWorkspaceDomain(
    organizationId: string,
    domain: string
  ): Promise<ServiceResponse<Organization>> {
    try {
      // Validate domain format
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
      if (!domainRegex.test(domain)) {
        return createErrorResponse("Invalid domain format");
      }

      // Check the org exists and is team or enterprise type
      const { data: org, error: orgError } = await typedSupabase
        .from("organizations")
        .select("org_type")
        .eq("id", organizationId)
        .single();

      if (orgError) { throw orgError; }

      const orgType = (org as unknown as Record<string, unknown>)?.org_type as string;
      if (orgType === 'personal') {
        return createErrorResponse("Personal organizations cannot link a Google Workspace domain");
      }

      // Update the organization with the domain (unique index will catch duplicates)
      const { data: updatedOrg, error } = await typedSupabase
        .from("organizations")
        .update({
          google_workspace_domain: domain.toLowerCase(),
          updated_at: new Date().toISOString(),
        } as any)
        .eq("id", organizationId)
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          return createErrorResponse("This Google Workspace domain is already linked to another organization");
        }
        throw error;
      }

      return createSuccessResponse(updatedOrg as Organization);
    } catch (error) {
      logger.error(`Error linking Google Workspace domain to organization ${organizationId}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to link Google Workspace domain');
    }
  }

  /**
   * Enable or disable auto-join for a Google Workspace domain.
   * When enabled, users signing in with a matching Google hd claim
   * are automatically added as members.
   *
   * @param organizationId - Organization ID
   * @param enabled - Whether auto-join should be enabled
   * @returns Promise with updated organization or error
   */
  async updateAutoJoinDomain(
    organizationId: string,
    enabled: boolean
  ): Promise<ServiceResponse<Organization>> {
    try {
      const { data: updatedOrg, error } = await typedSupabase
        .from("organizations")
        .update({
          auto_join_domain: enabled,
          updated_at: new Date().toISOString(),
        } as any)
        .eq("id", organizationId)
        .select()
        .single();

      if (error) { throw error; }

      return createSuccessResponse(updatedOrg as Organization);
    } catch (error) {
      logger.error(`Error updating auto-join for organization ${organizationId}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to update auto-join setting');
    }
  }

  /**
   * Remove a linked Google Workspace domain from an organization.
   *
   * @param organizationId - Organization ID
   * @returns Promise with updated organization or error
   */
  async unlinkGoogleWorkspaceDomain(
    organizationId: string
  ): Promise<ServiceResponse<Organization>> {
    try {
      const { data: updatedOrg, error } = await typedSupabase
        .from("organizations")
        .update({
          google_workspace_domain: null,
          google_workspace_verified_at: null,
          auto_join_domain: false,
          updated_at: new Date().toISOString(),
        } as any)
        .eq("id", organizationId)
        .select()
        .single();

      if (error) { throw error; }

      return createSuccessResponse(updatedOrg as Organization);
    } catch (error) {
      logger.error(`Error unlinking Google Workspace domain from organization ${organizationId}:`, { error });
      return createErrorResponse(parseErrorMessage(error) || 'Failed to unlink Google Workspace domain');
    }
  }
}

/**
 * Singleton instance of OrganizationService
 * Import this to use organization operations throughout the application
 *
 * @example
 * import { organizationService } from '@/services/core/organization.service';
 * const result = await organizationService.createOrganization({ name: "Acme Corp" });
 */
export const organizationService = new OrganizationService();
